import React, { Component } from "react";
import { Link } from "react-router-dom";
import LegalLayout from "../layouts/LegalLayout";

export default class Terms extends Component {
  render() {
    return (
      <LegalLayout title="Terms of Use">
        <p className="legal-updated">
          <b>Last updated:</b> July 2026
        </p>

        <p>
          These Terms of Use (&ldquo;Terms&rdquo;) are a legal agreement between
          you and Lucky Spin Games Inc. (&ldquo;Lucky Spin Games&rdquo;,
          &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;), a Nevada
          corporation. They govern your use of our mobile games and our website
          at luckyspingames.com.
        </p>

        <p>
          <b>
            Please read these Terms carefully. They include an agreement to
            resolve disputes through individual arbitration and a waiver of your
            right to participate in class actions (Section 13). By using the
            Service, you agree to these Terms.
          </b>
        </p>

        <hr />

        <h2>1. Definitions</h2>
        <p>
          <b>&ldquo;Service&rdquo;</b> means our mobile games &mdash; including
          Merge Treasure Hunt, Merge Journey, Picture Hunt: Hidden Objects, and
          Hidden Relics: Art Detective &mdash; together with any future games we
          release, our website at luckyspingames.com, and any related content,
          updates, and support services we provide.
        </p>
        <p>
          <b>&ldquo;App Store Provider&rdquo;</b> means the platform from which
          you downloaded a game, such as the Apple App Store, Google Play, the
          Amazon Appstore, or the Microsoft Store.
        </p>
        <p>
          <b>&ldquo;Virtual Items&rdquo;</b> means in-game currency (such as
          gems and coins), boosters, and any other virtual goods available
          within the Service.
        </p>

        <h2>2. Accepting These Terms</h2>
        <p>
          By downloading, installing, accessing, or using the Service, you agree
          to these Terms and to our{" "}
          <Link to="/privacy-policy">Privacy Policy</Link>. If you do not agree,
          please do not use the Service.
        </p>
        <p>
          Your use of the Service is also subject to the terms of the App Store
          Provider you downloaded it from. Where those terms conflict with these
          Terms in relation to purchases or billing, the App Store Provider's
          terms control.
        </p>

        <h2>3. Who May Use the Service</h2>
        <p>
          You must be at least 13 years old to use the Service. If you are
          between 13 and 18 (or the age of legal majority where you live), you
          may only use the Service with the involvement and consent of a parent
          or guardian, who agrees to these Terms on your behalf.
        </p>
        <p>
          Our games are intended for a general audience and are not designed for
          or directed to children under 13. If we learn that a child under 13
          has been using the Service, we will take steps to remove the account
          and associated data as described in our{" "}
          <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>

        <h2>4. Your Licence to Use the Service</h2>
        <p>
          We grant you a personal, limited, non-exclusive, non-transferable,
          revocable licence to use the Service for your own private,
          non-commercial entertainment. This licence lasts as long as you comply
          with these Terms.
        </p>
        <p>
          We and our licensors own all rights in the Service, including the
          games, artwork, characters, music, software, and trade marks. Nothing
          in these Terms transfers ownership of any of that to you.
        </p>

        <h2>5. Things You Agree Not to Do</h2>
        <p>You agree that you will not:</p>
        <ul>
          <li>
            Cheat, exploit bugs, or use unauthorised third-party software, bots,
            mods, or automation to gain an advantage;
          </li>
          <li>
            Reverse engineer, decompile, or attempt to extract the source code
            of the Service, except where the law expressly permits it;
          </li>
          <li>
            Attempt to gain unauthorised access to our systems, other players'
            accounts, or any data you are not entitled to;
          </li>
          <li>
            Buy, sell, trade, or transfer accounts or Virtual Items outside the
            Service, or attempt to convert them to real-world money;
          </li>
          <li>
            Interfere with the normal operation of the Service, including
            through denial-of-service attacks or the distribution of malware;
          </li>
          <li>
            Use the Service for any unlawful purpose or in breach of these Terms;
          </li>
          <li>
            Choose a display name that is offensive, impersonates another person,
            or infringes someone else's rights.
          </li>
        </ul>

        <h2>6. Your Account and Game Progress</h2>
        <p>
          You can play our games without creating an account. If you choose to
          connect your Facebook account or use Sign in with Apple, we use the
          identifier provided by that service to save and restore your game
          progress across devices. We describe exactly what we receive in our{" "}
          <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>
        <p>
          You are responsible for activity that takes place through your account
          and for keeping your linked login credentials secure.
        </p>
        <p>
          You may request deletion of your account and associated data at any
          time. Instructions are available in-game and on our website, and the
          process is described in our{" "}
          <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>

        <h2>7. Virtual Items and In-App Purchases</h2>
        <p>
          <b>7.1</b> The Service may include in-app purchases that allow you to
          buy Virtual Items such as in-game currency (&ldquo;In-App
          Purchases&rdquo;). You never need to make an In-App Purchase to play
          our games. While In-App Purchases may be offered to you within the
          Service and there is no cap on how many you can make, you can manage
          or switch off your ability to complete them in your device settings.
          You are responsible for managing your In-App Purchases and the amount
          you spend.
        </p>
        <p>
          <b>7.2</b> If you are under 18, you must have your parent's or
          guardian's permission before making any In-App Purchase. By completing
          an In-App Purchase, you confirm that you have that permission.
        </p>
        <p>
          <b>7.3</b> Information about managing or disabling In-App Purchases
          may be found in your App Store Provider's terms, your device's help
          settings, or online. If you are a parent or guardian and are concerned
          about a child making excessive In-App Purchases, we recommend
          reviewing the parental controls offered by the App Store Provider.
        </p>
        <p>
          <b>7.4</b> Virtual Items are licensed to you, not sold. You receive a
          limited right to use them within the Service only. Virtual Items have
          no monetary value, cannot be redeemed for cash or anything else of
          value, and cannot be transferred outside the Service. Once an In-App
          Purchase has been initiated it cannot be cancelled, and Virtual Items
          can only be consumed within the Service.
        </p>
        <p>
          <b>7.5</b> We may add, change, remove, or adjust the price or
          availability of Virtual Items, and we may change game balance and
          rules, at any time. If your account is terminated for breach of these
          Terms, any unused Virtual Items are forfeited without refund.
        </p>
        <p>
          <b>7.6</b> If an In-App Purchase fails to download or does not work
          properly, we will investigate once we become aware of the problem or
          you notify us. We will act reasonably in deciding whether to provide a
          replacement or a fix, and we will not charge you anything further to
          do so. If we cannot replace or repair it within a reasonable time and
          without significant inconvenience to you, we will authorise the App
          Store Provider to refund an amount up to the cost of that purchase.
          You may also request a refund directly from the App Store Provider.
        </p>
        <p>
          <b>7.7</b> All billing and payment processing is handled by the App
          Store Provider from whose platform you downloaded the Service, under
          that provider's terms. We do not receive or store your payment card
          details. For any payment-related issue, please contact the App Store
          Provider directly.
        </p>

        <h2>8. Advertising</h2>
        <p>
          Our games are supported in part by advertising. Ads are served through
          third-party advertising partners, and the data used to deliver them is
          described in our <Link to="/privacy-policy">Privacy Policy</Link>. We
          are not responsible for the content of third-party advertisements or
          for the products and services they promote.
        </p>

        <h2>9. Suspension and Termination</h2>
        <p>
          We may suspend or terminate your access to the Service, in whole or in
          part, if you breach these Terms, if we reasonably suspect fraud,
          cheating, or abuse, or if we are required to do so by law. Where it is
          reasonable and lawful to do so, we will give you notice.
        </p>
        <p>
          You may stop using the Service at any time by deleting the games from
          your devices, and you may request deletion of your data as described
          in Section 6.
        </p>
        <p>
          We may also discontinue a game or the Service as a whole. If we do, we
          will make reasonable efforts to give advance notice through the game
          or our website.
        </p>

        <h2>10. Updates and Changes to the Service</h2>
        <p>
          We regularly update our games to add content, fix problems, and improve
          performance. Some updates may be required for the Service to keep
          working. We may also modify or discontinue features at our discretion.
        </p>

        <h2>11. Disclaimers</h2>
        <p>
          The Service is provided on an &ldquo;as is&rdquo; and &ldquo;as
          available&rdquo; basis. To the fullest extent permitted by law, we
          disclaim all warranties, whether express or implied, including implied
          warranties of merchantability, fitness for a particular purpose, and
          non-infringement.
        </p>
        <p>
          We do not warrant that the Service will be uninterrupted, error-free,
          or free from harmful components, or that game progress and data will
          never be lost.
        </p>
        <p>
          Some jurisdictions do not allow the exclusion of certain warranties, so
          some of these exclusions may not apply to you. In that case, our
          liability is limited to the greatest extent permitted by law.
        </p>

        <h2>12. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, Lucky Spin Games and its
          officers, employees, and suppliers will not be liable for any
          indirect, incidental, special, consequential, or punitive damages, or
          for lost profits, lost data, or lost Virtual Items, arising out of or
          relating to your use of the Service.
        </p>
        <p>
          To the fullest extent permitted by law, our total liability for all
          claims relating to the Service will not exceed the greater of (a) the
          total amount you paid us or through an App Store Provider for the
          Service in the twelve months before the claim arose, or (b) US $100.
        </p>
        <p>
          Nothing in these Terms limits liability that cannot be limited under
          applicable law, including liability for fraud or for death or personal
          injury caused by negligence.
        </p>

        <h2>13. Dispute Resolution and Arbitration</h2>
        <p>
          <b>
            Please read this section carefully. It affects your legal rights,
            including your right to file a lawsuit in court and to participate
            in a class action.
          </b>
        </p>
        <p>
          <b>13.1 Informal resolution first.</b> Most concerns can be resolved
          quickly. Before starting formal proceedings, please contact us at{" "}
          <a href="mailto:support@luckyspingames.com">
            support@luckyspingames.com
          </a>{" "}
          and give us 30 days to try to resolve the issue with you.
        </p>
        <p>
          <b>13.2 Agreement to arbitrate.</b> If we cannot resolve a dispute
          informally, you and Lucky Spin Games agree that any dispute arising
          out of or relating to these Terms or the Service will be resolved by
          binding individual arbitration, rather than in court, except as set out
          in Section 13.4.
        </p>
        <p>
          <b>13.3 Class action waiver.</b> You and Lucky Spin Games agree that
          each may bring claims against the other only in an individual
          capacity, and not as a plaintiff or class member in any purported
          class or representative proceeding.
        </p>
        <p>
          <b>13.4 Exceptions.</b> Either party may bring an individual claim in
          small claims court, and either party may seek injunctive relief in
          court to protect intellectual property rights. If the class action
          waiver in Section 13.3 is found unenforceable as to a particular
          claim, that claim will proceed in court rather than in arbitration.
        </p>
        <p>
          <b>13.5 Your right to opt out.</b> You may opt out of this arbitration
          agreement by emailing{" "}
          <a href="mailto:support@luckyspingames.com">
            support@luckyspingames.com
          </a>{" "}
          within 30 days of first accepting these Terms, stating your name and
          that you wish to opt out of arbitration. Opting out will not affect any
          other part of these Terms.
        </p>

        <h2>14. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the State of Nevada, United
          States, without regard to its conflict of laws rules. Subject to
          Section 13, the state and federal courts located in Clark County,
          Nevada will have jurisdiction over any dispute not subject to
          arbitration.
        </p>
        <p>
          If you live in the European Economic Area, the United Kingdom, or
          another jurisdiction whose consumer protection laws give you rights
          that cannot be overridden by agreement, nothing in these Terms removes
          those rights, and you may be entitled to bring proceedings in your
          local courts.
        </p>

        <h2>15. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. When we do, we will
          revise the &ldquo;Last updated&rdquo; date at the top of this page. If
          the changes are material, we will give you reasonable notice through
          the Service or our website before they take effect. Continuing to use
          the Service after the changes take effect means you accept the updated
          Terms.
        </p>

        <h2>16. General</h2>
        <p>
          If any provision of these Terms is found to be unenforceable, the rest
          remains in effect. Our failure to enforce a provision is not a waiver
          of our right to enforce it later. You may not assign these Terms; we
          may assign them in connection with a merger, acquisition, or sale of
          assets.
        </p>
        <p>
          These Terms, together with our{" "}
          <Link to="/privacy-policy">Privacy Policy</Link>, are the entire
          agreement between you and Lucky Spin Games regarding the Service.
        </p>

        <h2>17. Contact Us</h2>
        <p>
          If you have questions about these Terms, please contact us at{" "}
          <a href="mailto:support@luckyspingames.com">
            support@luckyspingames.com
          </a>
          .
        </p>
        <p>
          Lucky Spin Games Inc.
          <br />
          848 N Rainbow Blvd 2527
          <br />
          Las Vegas, Nevada 89107
          <br />
          United States
        </p>

        <hr />

        <p>
          See also our <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>
      </LegalLayout>
    );
  }
}
